import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { scenarioLabApi } from "../../services/phase1Api";
import { Button } from "@/components/ui/button";
import {
  Loader2, Building2, School, ClipboardCheck, BarChart3, ArrowRight, ArrowLeft,
  CheckCircle2, ShieldAlert, Sparkles,
} from "lucide-react";

const NAVY = "#0b1220";
const RED = "#dc2626";

const BAND_COLOR = (band = "") => {
  const b = band.toLowerCase();
  if (b.includes("healthy")) return "#16a34a";
  if (b.includes("caution")) return "#d97706";
  if (b.includes("high")) return "#dc2626";
  return "#6b7280";
};

const Dot = ({ active, done }) => (
  <div className="h-2.5 rounded-full transition-all" style={{
    width: active ? 28 : 10,
    background: active ? RED : done ? "#94a3b8" : "#e2e8f0",
  }} />
);

const StepShell = ({ icon: Icon, kicker, title, children }) => (
  <div className="animate-[fadeIn_.4s_ease]">
    <div className="flex items-center gap-2 mb-2">
      <div className="h-9 w-9 rounded-xl flex items-center justify-center text-white" style={{ background: NAVY }}>
        <Icon className="h-4 w-4" />
      </div>
      <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: RED }}>{kicker}</span>
    </div>
    <h2 className="text-2xl sm:text-3xl font-bold mb-4" style={{ color: NAVY }}>{title}</h2>
    {children}
  </div>
);

export default function ScenarioTourPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    scenarioLabApi.publicTour(token).then(setData).catch(() => setErr(true));
  }, [token]);

  if (err) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6" data-testid="tour-not-found">
      <div className="text-center">
        <img src="/assets/myschoolrx-wordmark-600.png" alt="MySchoolRx" className="h-10 mx-auto mb-6" />
        <h1 className="text-xl font-bold" style={{ color: NAVY }}>This tour isn't available</h1>
        <p className="text-slate-500 mt-2">The link may be disabled or has expired. Ask your contact for a new link.</p>
      </div>
    </div>
  );
  if (!data) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
    </div>
  );

  const s = data.sample_school;
  const steps = [
    // 0 — Welcome
    (
      <StepShell icon={Sparkles} kicker="Guided Tour" title={`Welcome to ${data.scenario_name}`}>
        <p className="text-slate-600 text-base leading-relaxed max-w-xl">
          {data.description || "Take a quick, self-guided walkthrough of MySchoolRx using a sample portfolio."}
          {" "}This is a hands-off preview — nothing here is live, and no real school data is shown.
        </p>
        <div className="grid grid-cols-3 gap-3 mt-6 max-w-md">
          {[["Organizations", data.stats.org_count], ["Schools", data.stats.school_count],
            ["Avg. Score", data.stats.avg_score != null ? `${data.stats.avg_score}` : "—"]].map(([k, v]) => (
            <div key={k} className="rounded-xl border border-slate-200 bg-white p-3 text-center">
              <div className="text-2xl font-bold" style={{ color: NAVY }}>{v}</div>
              <div className="text-[11px] uppercase tracking-wide text-slate-400">{k}</div>
            </div>
          ))}
        </div>
      </StepShell>
    ),
    // 1 — Organizations
    (
      <StepShell icon={Building2} kicker="Step 1" title="The organizations">
        <p className="text-slate-600 mb-4 max-w-xl">Authorizers, networks and districts each oversee a group of schools. Here's who's in this portfolio.</p>
        <div className="space-y-2 max-w-xl" data-testid="tour-orgs">
          {data.organizations.map((o, i) => (
            <div key={i} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3">
              <div>
                <div className="font-semibold" style={{ color: NAVY }}>{o.name}</div>
                <div className="text-xs text-slate-400">{o.type_label}{o.state ? ` · ${o.state}` : ""}</div>
              </div>
              <div className="text-sm text-slate-500">{o.school_count} schools</div>
            </div>
          ))}
        </div>
      </StepShell>
    ),
    // 2 — Schools
    (
      <StepShell icon={School} kicker="Step 2" title="The schools">
        <p className="text-slate-600 mb-4 max-w-xl">Each school completes the diagnostic and receives an overall quality score and risk band.</p>
        <div className="space-y-2 max-w-2xl" data-testid="tour-schools">
          {data.schools.map((sc, i) => (
            <div key={i} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3">
              <div className="min-w-0">
                <div className="font-semibold truncate" style={{ color: NAVY }}>{sc.name}</div>
                <div className="text-xs text-slate-400">{sc.org_name || "Independent"}{sc.state ? ` · ${sc.state}` : ""}</div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-lg font-bold" style={{ color: NAVY }}>{sc.score != null ? sc.score : "—"}</span>
                <span className="text-[11px] font-semibold px-2 py-1 rounded-full text-white" style={{ background: BAND_COLOR(sc.risk_band) }}>{sc.risk_band || "No data"}</span>
              </div>
            </div>
          ))}
        </div>
      </StepShell>
    ),
    // 3 — Sample report
    (
      <StepShell icon={ClipboardCheck} kicker="Step 3" title="Inside a school's report">
        {s ? (
          <div className="max-w-2xl" data-testid="tour-sample">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 mb-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="text-lg font-bold" style={{ color: NAVY }}>{s.name}</div>
                  <div className="text-xs text-slate-400">Completion {s.completion_pct}%</div>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-extrabold" style={{ color: BAND_COLOR(s.risk_band) }}>{s.score}</div>
                  <div className="text-[11px] font-semibold" style={{ color: BAND_COLOR(s.risk_band) }}>{s.risk_band}</div>
                </div>
              </div>
            </div>
            <p className="text-slate-600 mb-3 text-sm">Scores are broken down across the eight pillars of school quality:</p>
            <div className="grid sm:grid-cols-2 gap-2">
              {s.pillars.map((p, i) => (
                <div key={i} className="rounded-xl border border-slate-200 bg-white px-3 py-2.5">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-medium truncate" style={{ color: NAVY }}>{p.title}</span>
                    <span className="text-sm font-bold" style={{ color: NAVY }}>{p.score != null ? p.score : "—"}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full" style={{ width: `${p.score || 0}%`, background: BAND_COLOR(p.risk_level) }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-slate-500"><ShieldAlert className="h-4 w-4" /> No scored diagnostic in this sample yet.</div>
        )}
      </StepShell>
    ),
    // 4 — Wrap up
    (
      <StepShell icon={BarChart3} kicker="You're all set" title="That's the diagnostic, end to end">
        <p className="text-slate-600 max-w-xl mb-6">
          From organizations down to a single school's pillar scores, MySchoolRx turns a self-assessment
          into clear, actionable oversight. Ready to run it with your own schools?
        </p>
        <div className="flex items-center gap-2 text-emerald-700 mb-6"><CheckCircle2 className="h-5 w-5" /> You've completed the guided tour.</div>
        <a href="/myschoolrx">
          <Button className="rounded-full px-6" style={{ background: RED }} data-testid="tour-cta">
            Learn more about the Diagnostic <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </a>
      </StepShell>
    ),
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="scenario-tour-page">
      <style>{"@keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}"}</style>
      {/* header */}
      <header className="sticky top-0 z-10 bg-white/90 backdrop-blur border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-5 h-14 flex items-center justify-between">
          <img src="/assets/myschoolrx-wordmark-600.png" alt="MySchoolRx" className="h-10 w-auto" />
          <span className="text-[11px] font-semibold uppercase tracking-widest px-2.5 py-1 rounded-full" style={{ background: "#fef2f2", color: RED }} data-testid="tour-demo-badge">
            Demo · Sample Data
          </span>
        </div>
      </header>

      {/* body */}
      <main className="flex-1 w-full max-w-4xl mx-auto px-5 py-8 sm:py-12">
        {steps[step]}
      </main>

      {/* footer nav */}
      <footer className="sticky bottom-0 bg-white/90 backdrop-blur border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-5 h-16 flex items-center justify-between">
          <Button variant="ghost" disabled={step === 0} onClick={() => setStep((s2) => s2 - 1)} data-testid="tour-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div className="flex items-center gap-1.5">
            {steps.map((_, i) => <Dot key={i} active={i === step} done={i < step} />)}
          </div>
          {step < steps.length - 1 ? (
            <Button onClick={() => setStep((s2) => s2 + 1)} style={{ background: NAVY }} className="rounded-full px-5" data-testid="tour-next-btn">
              Next <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button variant="ghost" onClick={() => setStep(0)} data-testid="tour-restart-btn">Restart</Button>
          )}
        </div>
      </footer>
    </div>
  );
}
